import type { CollaboratorPublic } from '@/features/Collaborators/Data/hooks/useCollaboratorsPublicQuery';
import type { VacationBalance, VacationRequest } from '../models/vacation.types';
import type { VacationApprovedPeriod, VacationDpRow } from '../models/vacationDpBoard.types';
import {
    findOpenVacationRequest,
    isApprovedVacationRequest,
    isDraftVacationRequest,
    resolveManagerEmail,
} from './vacationRequestStatus';
import { resolveScheduleDeadline } from './vacationScheduleDeadline';
import { resolveApprovalDeadline } from './vacationApprovalDeadline';
import { getApprovedSegments } from '../../../../shared/vacations/effectiveSegments';

export interface DpBoardSources {
    balances: VacationBalance[];
    collaborators: Map<string, CollaboratorPublic>;
    requests: VacationRequest[];
}

export function buildDpRows({ balances, collaborators, requests }: DpBoardSources): VacationDpRow[] {
    const requestsByCollaborator = groupRequestsByCollaborator(requests);
    const namesByEmail = indexNamesByEmail(collaborators);

    return balances.map((balance) => {
        const collaborator = collaborators.get(balance.collaboratorId);
        const schedule = resolveScheduleDeadline(balance, balance.nextDeadline);

        // Draft: não enxergar enquanto o colaborador ainda pode apagar.
        const collaboratorRequests = (requestsByCollaborator.get(balance.collaboratorId) ?? []).filter(
            (candidate) => !isDraftVacationRequest(candidate)
        );
        const request = findOpenVacationRequest(collaboratorRequests);

        // Prazo: preferir o da solicitação aberta, senão usar o do saldo
        const requestDeadline = request?.concessiveDeadline ?? null;
        const balanceDeadline = schedule.variant === 'deadline' ? schedule.date : null;
        const isRequestDeadline = requestDeadline !== null;
        const finalDeadline = requestDeadline ?? balanceDeadline;

        // Prazo de aprovação (início − 30 dias): quando há solicitação aguardando decisão
        let approvalDeadline: string | null = null;
        if (request && request.segments && (request.status === 'PendingManager' || request.status === 'PendingDP' || request.status === 'PendingClient' || request.status === 'PendingCorrection')) {
            const deadline = resolveApprovalDeadline(request.segments);
            if (deadline) {
                approvalDeadline = deadline.toISOString().split('T')[0];
            }
        }

        return {
            collaboratorId: balance.collaboratorId,
            collaboratorName: collaborator?.name ?? balance.collaboratorEmail,
            collaboratorEmail: balance.collaboratorEmail,
            collaboratorRole: collaborator?.positionName ?? null,
            collaboratorSquad: collaborator?.departmentName ?? null,
            balanceDays: balance.balanceDays,
            expiredDays: balance.expiredDays,
            pastLimitDays: balance.pastLimitDays ?? 0,
            daysWithinDeadline: Math.max(0, balance.balanceDays - balance.expiredDays - (balance.pastLimitDays ?? 0)),
            scheduledDays: sumScheduledDays(balance),
            pendingDays: sumPendingDays(balance),
            concessiveDeadline: finalDeadline,
            legalDeadline: balance.nextLegalDeadline ?? null,
            deadlineIsProjected: false,
            legacyTakenDays: balance.legacyTakenDays,
            legacyScheduledDays: balance.legacyScheduledDays,
            monthsWorked: balance.monthsWorked,
            unallocatedDays: balance.unallocatedDays,
            request,
            eligible: balance.eligible,
            managerName: resolveManagerName(request, namesByEmail),
            approvalDeadline,
            approvedPeriods: collectApprovedPeriods(collaboratorRequests),
        };
    });
}

function collectApprovedPeriods(requests: VacationRequest[]): VacationApprovedPeriod[] {
    const periods = requests
        .filter(isApprovedVacationRequest)
        .flatMap((request) => {
            const approvedSegments = getApprovedSegments(request).filter(
                (segment) => Boolean(segment.startDate) && Boolean(segment.endDate)
            );
            return approvedSegments.map((segment, index) => ({
                requestId: request.id,
                status: request.status,
                startDate: segment.startDate,
                endDate: segment.endDate,
                days: segment.days,
                hasApprovedSell: request.sellRequest.status === 'Approved',
                segmentOrder: index + 1,
                segmentCount: approvedSegments.length,
                modality: request.modality,
                requestedAt: request.requestedAt?.toDate?.()?.toISOString() ?? null,
            }));
        });

    return periods.sort((a, b) => a.startDate.localeCompare(b.startDate));
}

function sumScheduledDays(balance: VacationBalance): number {
    return balance.periods.reduce((total, period) => total + (period.scheduledDays || 0), 0);
}

function sumPendingDays(balance: VacationBalance): number {
    return balance.periods.reduce((total, period) => total + (period.pendingDays || 0), 0);
}

function indexNamesByEmail(collaborators: Map<string, CollaboratorPublic>): Map<string, string> {
    const index = new Map<string, string>();

    collaborators.forEach((collaborator) => {
        if (collaborator.email) index.set(collaborator.email.toLowerCase(), collaborator.name);
    });

    return index;
}

function resolveManagerName(
    request: VacationRequest | null,
    namesByEmail: Map<string, string>
): string | null {
    const email = resolveManagerEmail(request);
    if (!email) return null;

    return namesByEmail.get(email.toLowerCase()) ?? null;
}

function groupRequestsByCollaborator(requests: VacationRequest[]): Map<string, VacationRequest[]> {
    const grouped = new Map<string, VacationRequest[]>();

    requests.forEach((request) => {
        const current = grouped.get(request.collaboratorId);
        if (current) current.push(request);
        else grouped.set(request.collaboratorId, [request]);
    });

    return grouped;
}
